import React, {
    FC,
    ReactNode,
    useMemo,
    useState,
    createContext,
    useContext,
    useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { PessoaAPI } from '../http-requests';
import { AuthSession } from '../../store/ducks/auth';
import LoadingPage from '../../modules/consulta/components/LoadingPage';
import { useAppContext } from './AppContextProvider';

export type LangType = 'pt' | 'en' | 'es';

interface AppMobileContextProps {
    renderedByApp: boolean;
    sendMessageToApp: (data: AppMessageDataType) => void
    sendUserDataToApp: (userData: any) => void
    sendLogToApp: (log: any) => void
    removeUUIDFromApp: () => void
    sendPdfToApp: (base64: string, filename: string) => void
    openPdfUrlInApp: (url: string, restrito?: boolean) => void
}

const AppMobileContext = createContext<AppMobileContextProps | undefined>(
    undefined,
);

interface AppMessageDataType {
    id: string;
    payload?: any;
}

interface AppMobileContextProviderProps {
    children: ReactNode;
    statusRx: any;
    guiasPesquisaRx: any;
    match: any;
    consultaRx: any;
    authData: any;
    userSessionRx: any;
    userLocalRx: any;
}

const AppMobileContextProvider: FC<AppMobileContextProviderProps> = ({
    children,
    statusRx,
    guiasPesquisaRx,
    consultaRx,
    authData,
    userSessionRx,
    userLocalRx,
}) => {
    const [renderedByApp, setRenderedByApp] = useState<boolean>(false);
    const user = userSessionRx || userLocalRx;
    const [waitProcess, setWaitProcess] = useState(true)
    const dispatch = useDispatch()
    const { changeLang, lang } = useAppContext()
    const [loadedAppLang, setLoadAppLanguage] = useState(false)
    const location = useLocation()
    const [lastChangedPageName, setLastChangedPageName] = useState<string|undefined>()

    //Altera o label do topo do app baseado na pagina
    useEffect(() => {
        const { pathname } = location;
        let nextPageName;
        if(!pathname.startsWith('/login')){
            // eslint-disable-next-line no-nested-ternary
            nextPageName = pathname.startsWith('/meupergamum') ? 'meupergamum' :
                pathname.startsWith('/login') ? 'login':
                'catalogo'
            if(lastChangedPageName !== nextPageName){
                setLastChangedPageName(nextPageName)
                changeAppPageName(nextPageName)
            }
        }
    }, [location.pathname, lastChangedPageName]);

    useEffect(() => {
        if(loadedAppLang){
            changeAppLanguage(lang)
        }
    }, [lang, loadedAppLang]);

    /**
     * Autentica
     */
    useEffect(() => {
        const { ReactNativeWebView, appUUID } = window as any;

        if(ReactNativeWebView && appUUID){
            PessoaAPI.authAppUuid(appUUID).then((response) => {
                const { data } = response;
                if(data){
                    const { user, authPermissions } = data;
                    const credentials = {
                        token: data.credentials.access_token,
                        refresh: data.credentials.refresh_token,
                    };

                    // Seta as novas credenciais na sessao
                    dispatch(
                        AuthSession.storeData({
                            credentials,
                            user,
                            authPermissions,
                        }),
                    );
                }
                setWaitProcess(false)
            }).catch((error) => {
                sendLogToApp({error})
                setWaitProcess(false)
            })
        }else{
            setWaitProcess(false)
        }
    }, []);

    useEffect(() => {
        const { ReactNativeWebView } = window as any;

        /**
         * Tratar as mensagens do app aqui ...
         * @param event
         */
        const onMessage = (event: any) => {
            const { data } = event;
            if (data) {
                try {
                    const parsedData = JSON.parse(data) as AppMessageDataType;
                    const { id, payload } = parsedData;
                    switch (id) {
                        case 'change_front_language':
                            if(['en', 'pt', 'es'].includes(payload)){
                                changeLang(payload)
                                setTimeout(() => setLoadAppLanguage(true), 500)
                            }
                            break;
                        case 'log':
                            sendLogToApp(payload)
                            break;
                        default:
                            break;
                    }
                } catch (e) {}
            }
        };

        const listenerTarget = /iPhone|iPad|iPod/i.test(navigator.userAgent) ? window : document;


        if (ReactNativeWebView) {
            setRenderedByApp(true);
            listenerTarget.addEventListener('message', onMessage);
        }

        return () => {
            listenerTarget.removeEventListener('message', onMessage);
        };
    }, []);

    function changeAppPageName(pageName: string){
        sendMessageToApp({
            id: 'change_page_name',
            payload: pageName
        })
    }

    function changeAppLanguage(language: string){
        sendMessageToApp({
            id: 'change_language',
            payload: language
        })
    }
    function sendMessageToApp(data: AppMessageDataType) {
        const { ReactNativeWebView } = window as any;
        if (ReactNativeWebView) {
            ReactNativeWebView.postMessage(JSON.stringify(data));
        }
    }

    const sendUserDataToApp = (userData: any) => {
        sendMessageToApp({
            id: 'user_data',
            payload: userData
        })
    }


    const removeUUIDFromApp = () => {
        sendMessageToApp({
            id: 'remove_app_uuid',
        })
    }

    const sendLogToApp = (log: any) => {
        sendMessageToApp({
            id: 'app_log',
            payload: {log}
        })
    }

    /**
     * Entrega ao app um PDF gerado aqui no navegador, para que ele abra no
     * visualizador nativo. Vai em base64 porque o documento existe apenas em
     * memoria: nao ha URL que o app possa baixar.
     */
    const sendPdfToApp = (base64: string, filename: string) => {
        sendMessageToApp({
            id: 'open-pdf',
            payload: { base64, filename }
        })
    }

    /**
     * Entrega ao app um PDF que existe como URL, para abrir no visualizador
     * nativo em vez do modal.
     *
     * Aqui o conteudo nao trafega pela ponte: o app baixa a URL por conta
     * propria, o que evita o teto de tamanho do base64 e serve a documentos de
     * acervo, que costumam ser grandes. O endereco e absolutizado porque o
     * catalogo monta caminhos relativos e o app nao compartilha a origem.
     */
    const openPdfUrlInApp = (url: string, restrito = false) => {
        if (!url) return;

        let absoluteUrl = url;

        try {
            absoluteUrl = new URL(url, window.location.origin).href;
        } catch {
            // URL inesperada: segue como veio, o app decide o que fazer.
        }

        sendMessageToApp({
            id: 'external-link',
            // `restrito` acompanha o documento porque o visualizador do app
            // oferece compartilhamento pelo sistema, e o modal daqui nao
            // oferece de proposito: material de acesso restrito pode ser lido,
            // nao levado embora.
            payload: { url: absoluteUrl, restrito }
        })
    }

    const memoizedData: AppMobileContextProps = useMemo(
        () => ({
            sendMessageToApp,
            renderedByApp,
            sendUserDataToApp,
            sendLogToApp,
            removeUUIDFromApp,
            sendPdfToApp,
            openPdfUrlInApp
        }),
        [renderedByApp, waitProcess],
    );

    return (
        <AppMobileContext.Provider value={memoizedData}>
            {waitProcess ? <LoadingPage /> :  children}
        </AppMobileContext.Provider>
    );
};

export const useAppMobileContext = (): AppMobileContextProps => {
    const context = useContext(AppMobileContext) as AppMobileContextProps;
    if (context === undefined)
        throw new Error(
            'useAppMobileContext must be used within a AppMobileContextProvider',
        );
    return context;
};
const mapStateToProps = (state: any) => ({
    statusRx: state.pageConfig.status,
    guiasPesquisaRx: state.pageConfig.guiasPesquisa,
    consultaRx: state.pesquisa.consulta,
    authData: state.auth || state.authPersist,
    userSessionRx: state.auth.user,
    userLocalRx: state.authPersist.user,
});
export default connect(mapStateToProps)(AppMobileContextProvider);
